<script setup lang="ts">
import { computed } from 'vue'

import type { ImpactColor, ImpactType, TotalImpact } from '@/api/interfaces'
import type { IconStyleType } from '@/api/interfaces'
import type { Comparison, WaterComparison } from '@/stores/interfaces/PublicData'
import { usePublicData } from '@/stores/PublicData'
import { useEditorStore } from '@/stores/Editor'

import PublicComparisonCard from '@/components/public-page/PublicComparisonCard.vue'
import BackgroundIcon from '@/components/icons/backgrounds/BackgroundIcon.vue'

interface Props {
  topStatData: TotalImpact
  impactType: ImpactType
  colorSettings: Record<ImpactType, ImpactColor>
  iconStyle: IconStyleType
}

const props = defineProps<Props>()

const publicStore = usePublicData()
const editorStore = useEditorStore()

const sortDisplayOrder = (comparison: Comparison) => {
  if (props.impactType !== 'water') return comparison

  const { litres, fetchingWater, brighterEducation } = comparison as WaterComparison
  return {
    fetchingWater,
    litres,
    brighterEducation,
  }
}
const comparison = computed<Comparison>(() => {
  return sortDisplayOrder(publicStore.getComparisons(props.impactType))
})
</script>

<template>
  <section
    :class="['public-comparison-cards', impactType, `style-${editorStore.editor.icon.style}`]"
  >
    <BackgroundIcon
      v-if="impactType === 'trees'"
      class="background-icon"
      :align="$route.name === 'CommunityPage' ? 'right' : 'left'"
      variant="outline"
      :icon-type="editorStore.editor.background.icon"
    />
    <v-container>
      <div class="public-comparison-cards-wrapper">
        <h2 class="section-subtitle">{{ $t(`PublicComparisonCardRow.${impactType}`) }}:</h2>
        <div :class="['public-comparison-cards-list', editorStore.editor.icon.style]">
          <PublicComparisonCard
            v-for="(value, key) in comparison"
            :key="key"
            :color-settings="colorSettings[impactType]"
            :icon-style="iconStyle"
            :impact-type="impactType"
            :amount="typeof value === 'number' ? { value } : value"
            :comparisonType="key"
            :class="key"
          />
        </div>
      </div>
    </v-container>
    <BackgroundIcon
      v-if="impactType === 'carbon'"
      class="background-icon"
      :align="$route.name === 'CommunityPage' ? 'left' : 'right'"
      variant="outline"
      :icon-type="editorStore.editor.background.icon"
    />
  </section>
</template>

<style lang="scss">
.public-comparison-cards {
  position: relative;

  @include media('md-and-up') {
    margin-bottom: 80px;
  }

  &.trees {
    .background-icon {
      display: none;

      .community-page & {
        @include media('md-and-up') {
          top: 175%;
          right: -200px;
          left: auto;
          z-index: 0;
          width: 600px;
          height: 657px;
          transform: scaleY(-1) rotate(-25deg);
        }
      }

      @include media('md-and-up') {
        display: block;
        position: absolute;
        top: 0;
        left: -65px;
        z-index: 0;
        width: 340px;
        height: 270px;
      }
    }
  }

  &.carbon {
    .background-icon {
      display: none;

      .community-page & {
        @include media('md-and-up') {
          left: 40px;
          z-index: 0;
          transform: scale(1.75) rotate(-25deg);
        }
      }

      @include media('md-and-up') {
        display: block;
        position: absolute;
        top: -80px;
        right: -95px;
        z-index: 0;
        width: 330px;
        height: 265px;
        transform: scaleY(-1);
      }
    }
  }

  &.style-isometric,
  &.style-flat {
    .section-subtitle {
      margin-bottom: 42px;
    }
  }

  &:nth-child(even) {
    .public-comparison-cards-list {
      &.isometric,
      &.flat {
        @include media('md-and-up') {
          grid-template-columns: 2fr 5fr;

          .public-comparison-card {
            &:nth-child(1) {
              grid-column: 2;
              grid-row: 1;

              .public-comparison-card-icon-wrapper {
                order: 0;
              }
            }

            &:nth-child(2) {
              margin-bottom: 15%;
              margin-top: 0;
              grid-row: span 2;
              grid-column: 1;

              .public-comparison-card-icon-wrapper {
                order: 1;
                margin-top: 0;
                margin-bottom: -130px;
                text-align: center;
              }
            }

            &:nth-child(3) {
              grid-column: 2;
              grid-row: 2;

              .public-comparison-card-icon-wrapper {
                order: 1;
              }
            }
          }
        }
      }
    }
  }
}

.public-comparison-cards-wrapper {
  position: relative;
  z-index: 1;

  .section-subtitle {
    margin-bottom: 16px;
    text-align: center;
  }
}

.public-comparison-cards-list {
  &.simple {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    width: 100%;

    @include media('sm-and-up') {
      grid-template-columns: 1fr 1fr;
    }

    @include media('md-and-up') {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  &.flat,
  &.isometric {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;

    @include media('sm-and-up') {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      gap: 16px;
    }

    @include media('md-and-up') {
      display: grid;
      max-width: 100%;
      grid-template-columns: 5fr 2fr;
      grid-auto-rows: 1fr;
      gap: 50px;
    }

    .public-comparison-card {
      @include media('sm-and-down') {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 16px;
      }

      @include media('sm-and-up') {
        width: 49%;
      }

      @include media('md-and-up') {
        width: 100%;
      }

      .public-comparison-card-icon {
        @include media('sm-and-down') {
          max-width: 120px;
          max-height: 130px;
        }
      }

      .public-comparison-card-amount {
        font-size: 54px;
        font-weight: 700;
        margin-bottom: 0;

        .community-page & {
          font-size: 38px;
        }

        @include media('md-and-up') {
          font-size: 64px;
          line-height: 78px;
          margin-bottom: 8px;
        }
      }

      .public-comparison-card-description {
        font-size: 16px;
        margin-bottom: 5px;

        @include media('md-and-up') {
          font-size: 18px;
          margin-bottom: 12px;
        }
      }

      &:nth-child(1) {
        @include media('md-and-up') {
          grid-column: 1;
          grid-row: 1;
        }

        .public-comparison-card-icon-wrapper {
          order: 1;
        }

        .public-comparison-card-content {
          margin-right: auto;
        }
      }

      &:nth-child(2) {
        @include media('md-and-up') {
          display: flex;
          flex-direction: column;
          justify-content: space-evenly;
          height: 90%;
          margin-top: 15%;
          grid-column: 2;
          grid-row: span 2;
        }

        .public-comparison-card-content {
          text-align: right;

          @include media('md-and-up') {
            text-align: center;
          }
        }

        .public-comparison-card-icon-wrapper {
          @include media('md-and-up') {
            text-align: center;
            transform: scale(1.2);
            margin-top: -155px;
          }
        }
      }

      &:nth-child(3) {
        @include media('md-and-up') {
          grid-column: 1;
          grid-row: 2;
        }

        .public-comparison-card-content {
          margin-left: auto;
        }

        .public-comparison-card-icon-wrapper {
          @include media('sm-and-down') {
            order: 1;
          }
        }
      }

      &:nth-child(1),
      &:nth-child(3) {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;

        .public-comparison-card-icon-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 0;
          width: 35%;

          @include media('md-and-up') {
            width: 45%;
            transform: scale(1.3);
          }
        }

        .public-comparison-card-content {
          width: 65%;

          @include media('md-and-up') {
            width: 55%;
          }
        }
      }
    }
  }
}
</style>
